import { apiFetch, useMockApi } from '@/lib/api/client'
import { mapRatingDto } from '@/lib/api/mappers'
import {
  computeMockUserScore,
  saveMockUserScoreOverride,
} from '@/lib/catalog/mock-user-score'
import { MOCK_COMPARE_RATING_TARGETS } from '@/lib/compare/rating-targets'
import type { CreateRatingRequest, RatingDto } from '@/types/compare'

const RATINGS_STORAGE_KEY = 'modelscout_mock_ratings'

function loadRatings(): Record<string, RatingDto> {
  if (typeof sessionStorage === 'undefined') return {}

  const raw = sessionStorage.getItem(RATINGS_STORAGE_KEY)
  if (!raw) return {}

  try {
    return JSON.parse(raw) as Record<string, RatingDto>
  } catch {
    return {}
  }
}

function saveRatings(ratings: Record<string, RatingDto>) {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(RATINGS_STORAGE_KEY, JSON.stringify(ratings))
}

export interface RatingsApi {
  createRating(request: CreateRatingRequest): Promise<RatingDto>
}

class MockRatingsApi implements RatingsApi {
  private ratings = loadRatings()

  async createRating(request: CreateRatingRequest): Promise<RatingDto> {
    const existing = Object.values(this.ratings).find(
      (rating) => rating.modelResponseId === request.modelResponseId,
    )

    const rating: RatingDto = {
      id: existing?.id ?? `rating-${crypto.randomUUID()}`,
      modelResponseId: request.modelResponseId,
      ratingStars: request.ratingStars,
      thumbs: request.thumbs,
      isBestAnswer: request.isBestAnswer ?? false,
      comment: request.comment,
      createdAt: new Date().toISOString(),
    }

    this.ratings[rating.id] = rating
    saveRatings(this.ratings)

    if (rating.isBestAnswer) {
      for (const other of Object.values(this.ratings)) {
        if (other.modelResponseId !== rating.modelResponseId && other.isBestAnswer) {
          other.isBestAnswer = false
        }
      }
      saveRatings(this.ratings)
    }

    this.syncCatalogUserScore(rating.modelResponseId)

    return structuredClone(rating)
  }

  private syncCatalogUserScore(modelResponseId: string) {
    const target = MOCK_COMPARE_RATING_TARGETS[modelResponseId]
    if (!target) return

    const responseRatings = Object.values(this.ratings).filter(
      (entry) => MOCK_COMPARE_RATING_TARGETS[entry.modelResponseId]?.modelId === target.modelId,
    )

    saveMockUserScoreOverride(
      target.modelId,
      target.categoryId,
      computeMockUserScore(responseRatings),
    )
  }
}

class HttpRatingsApi implements RatingsApi {
  async createRating(request: CreateRatingRequest): Promise<RatingDto> {
    const body = await apiFetch<unknown>('/api/v1/ratings', {
      method: 'POST',
      body: JSON.stringify({
        model_response_id: request.modelResponseId,
        rating_stars: request.ratingStars,
        thumbs: request.thumbs,
        is_best_answer: request.isBestAnswer ?? false,
        comment: request.comment,
      }),
    })

    return mapRatingDto(body)
  }
}

export const ratingsApi: RatingsApi = useMockApi() ? new MockRatingsApi() : new HttpRatingsApi()

export function resetMockRatingsStorage() {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(RATINGS_STORAGE_KEY)
}